'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

// Maturity level definitions
const MATURITY_LEVELS = [
  { level: 1, name: 'Initial', color: 'text-red-500', description: 'Ad-hoc, reactive approach', bgColor: 'bg-red-500/20', borderColor: 'border-red-500' },
  { level: 2, name: 'Developing', color: 'text-accent', description: 'Basic practices in place', bgColor: 'bg-accent-muted', borderColor: 'border-accent' },
  { level: 3, name: 'Defined', color: 'text-yellow-500', description: 'Standardized and documented', bgColor: 'bg-yellow-500/20', borderColor: 'border-yellow-500' },
  { level: 4, name: 'Managed', color: 'text-blue-400', description: 'Measured and controlled', bgColor: 'bg-blue-400/20', borderColor: 'border-blue-400' },
  { level: 5, name: 'Optimizing', color: 'text-green-500', description: 'Continuous improvement culture', bgColor: 'bg-green-500/20', borderColor: 'border-green-500' },
];

interface AssessmentCategory {
  id: string;
  name: string;
  icon: string;
  weight: number;
  criteria: { level: number; description: string; indicators: string[] }[];
}

// CTO Personal Assessment Categories
const CTO_CATEGORIES: AssessmentCategory[] = [
  {
    id: 'technical',
    name: 'Technical Excellence',
    icon: '🔧',
    weight: 0.20,
    criteria: [
      { level: 1, description: 'Limited technical involvement', indicators: ['Rarely reviews code', 'Disconnected from tech decisions', 'No architecture involvement'] },
      { level: 2, description: 'Basic technical oversight', indicators: ['Occasional code reviews', 'High-level tech input', 'Aware of major systems'] },
      { level: 3, description: 'Active technical leadership', indicators: ['Regular code/design reviews', 'Guides architecture decisions', 'Maintains technical credibility'] },
      { level: 4, description: 'Deep technical engagement', indicators: ['Participates in complex designs', 'Mentors senior engineers', 'Drives technical standards'] },
      { level: 5, description: 'Technical visionary', indicators: ['Industry-leading expertise', 'Influences tech strategy company-wide', 'Recognized technical thought leader'] },
    ],
  },
  {
    id: 'leadership',
    name: 'Leadership & Vision',
    icon: '🎯',
    weight: 0.20,
    criteria: [
      { level: 1, description: 'Reactive management', indicators: ['No clear vision', 'Firefighting mode', 'Limited stakeholder communication'] },
      { level: 2, description: 'Emerging leadership', indicators: ['Basic team direction', 'Some strategic thinking', 'Regular updates to leadership'] },
      { level: 3, description: 'Clear direction', indicators: ['Defined tech vision', 'Aligns team with goals', 'Effective communication to all levels'] },
      { level: 4, description: 'Inspiring leadership', indicators: ['Compelling tech vision', 'Drives cultural change', 'Strong cross-functional influence'] },
      { level: 5, description: 'Transformational leader', indicators: ['Industry-shaping vision', 'Builds high-performing culture', 'Board-level strategic impact'] },
    ],
  },
  {
    id: 'team',
    name: 'Team Management',
    icon: '👥',
    weight: 0.15,
    criteria: [
      { level: 1, description: 'Ad-hoc team management', indicators: ['High turnover', 'No hiring process', 'Limited 1:1s'] },
      { level: 2, description: 'Basic team structure', indicators: ['Some hiring process', 'Regular 1:1s', 'Basic performance reviews'] },
      { level: 3, description: 'Structured management', indicators: ['Strong hiring bar', 'Career development paths', 'Effective delegation'] },
      { level: 4, description: 'High-performing teams', indicators: ['Top talent attraction', 'Leadership bench strength', 'Culture of growth'] },
      { level: 5, description: 'Organization builder', indicators: ['Industry-leading team', 'Multiplier effect on talent', 'Sustainable scaling model'] },
    ],
  },
  {
    id: 'operations',
    name: 'Operational Excellence',
    icon: '⚙️',
    weight: 0.15,
    criteria: [
      { level: 1, description: 'Chaotic operations', indicators: ['No processes', 'Frequent incidents', 'No metrics'] },
      { level: 2, description: 'Basic processes', indicators: ['Some documentation', 'Basic metrics tracking', 'Manual workflows'] },
      { level: 3, description: 'Defined operations', indicators: ['Documented processes', 'Key metrics monitored', 'Incident management'] },
      { level: 4, description: 'Optimized operations', indicators: ['Data-driven decisions', 'Automated workflows', 'Predictive insights'] },
      { level: 5, description: 'Operational excellence', indicators: ['Self-service operations', 'Platform thinking', 'Continuous optimization'] },
    ],
  },
  {
    id: 'strategy',
    name: 'Strategic Alignment',
    icon: '🧭',
    weight: 0.15,
    criteria: [
      { level: 1, description: 'Tech isolated from business', indicators: ['No business context', 'Pure tech focus', 'Limited exec interaction'] },
      { level: 2, description: 'Basic business awareness', indicators: ['Understands business goals', 'Reactive to business needs', 'Some exec collaboration'] },
      { level: 3, description: 'Business-aligned tech', indicators: ['Tech roadmap aligned to business', 'Proactive planning', 'Strong exec relationships'] },
      { level: 4, description: 'Strategic partner', indicators: ['Influences business strategy', 'Tech enables new opportunities', 'Trusted advisor to CEO'] },
      { level: 5, description: 'Business transformation driver', indicators: ['Tech-driven growth', 'Creates competitive advantage', 'Shapes company direction'] },
    ],
  },
  {
    id: 'external',
    name: 'External Impact',
    icon: '🌟',
    weight: 0.15,
    criteria: [
      { level: 1, description: 'No external presence', indicators: ['No public profile', 'Poor employer brand', 'Recruitment challenges'] },
      { level: 2, description: 'Emerging presence', indicators: ['Some community involvement', 'Basic employer branding', 'Ad-hoc recruiting'] },
      { level: 3, description: 'Active engagement', indicators: ['Conference talks', 'Strong employer brand', 'Inbound recruiting'] },
      { level: 4, description: 'Industry influence', indicators: ['Recognized expert', 'Magnetic employer brand', 'Community leader'] },
      { level: 5, description: 'Industry shaper', indicators: ['Thought leader', 'Industry standards contributor', 'Destination employer'] },
    ],
  },
];

// CTO Team Function Categories
const TEAM_CATEGORIES: AssessmentCategory[] = [
  {
    id: 'engineering',
    name: 'Engineering Function',
    icon: '💻',
    weight: 0.25,
    criteria: [
      { level: 1, description: 'Ad-hoc development', indicators: ['No standards', 'Cowboy coding', 'No code review'] },
      { level: 2, description: 'Basic practices', indicators: ['Some standards', 'Occasional reviews', 'Basic git workflow'] },
      { level: 3, description: 'Defined practices', indicators: ['Coding standards enforced', 'Required code review', 'Feature branching'] },
      { level: 4, description: 'Advanced practices', indicators: ['TDD/BDD adoption', 'Pair programming', 'Trunk-based development'] },
      { level: 5, description: 'Engineering excellence', indicators: ['Mob programming', 'Continuous delivery', 'Innovation time'] },
    ],
  },
  {
    id: 'architecture',
    name: 'Architecture Function',
    icon: '🏗️',
    weight: 0.20,
    criteria: [
      { level: 1, description: 'No architecture', indicators: ['Monolithic chaos', 'No documentation', 'Tech debt crisis'] },
      { level: 2, description: 'Basic structure', indicators: ['Some documentation', 'Basic patterns', 'Reactive tech debt'] },
      { level: 3, description: 'Defined architecture', indicators: ['Clear patterns', 'ADRs documented', 'Managed tech debt'] },
      { level: 4, description: 'Evolved architecture', indicators: ['Service-oriented', 'Strategic tech debt', 'Evolutionary design'] },
      { level: 5, description: 'Architecture excellence', indicators: ['Event-driven systems', 'Domain-driven design', 'Platform architecture'] },
    ],
  },
  {
    id: 'devops',
    name: 'DevOps/SRE Function',
    icon: '🔄',
    weight: 0.15,
    criteria: [
      { level: 1, description: 'Manual operations', indicators: ['No CI/CD', 'Manual deployments', 'No monitoring'] },
      { level: 2, description: 'Basic automation', indicators: ['Basic CI', 'Scripted deploys', 'Simple monitoring'] },
      { level: 3, description: 'Automated pipelines', indicators: ['Full CI/CD', 'Infrastructure as code', 'Observability'] },
      { level: 4, description: 'SRE practices', indicators: ['SLO/SLI tracking', 'Chaos engineering', 'Incident management'] },
      { level: 5, description: 'Platform engineering', indicators: ['Self-service platform', 'Auto-remediation', 'FinOps integration'] },
    ],
  },
  {
    id: 'qa',
    name: 'Quality Assurance Function',
    icon: '✅',
    weight: 0.15,
    criteria: [
      { level: 1, description: 'No formal QA', indicators: ['Manual testing only', 'No test strategy', 'Bugs in production'] },
      { level: 2, description: 'Basic testing', indicators: ['Some automation', 'Test plans exist', 'Bug tracking'] },
      { level: 3, description: 'Comprehensive QA', indicators: ['Automated testing', 'Quality gates', 'Shift-left testing'] },
      { level: 4, description: 'Advanced QA', indicators: ['TDD/BDD', 'Performance testing', 'Quality metrics'] },
      { level: 5, description: 'Quality engineering', indicators: ['Continuous testing', 'Production testing', 'Quality culture'] },
    ],
  },
  {
    id: 'security',
    name: 'Security Function',
    icon: '🔒',
    weight: 0.15,
    criteria: [
      { level: 1, description: 'Security afterthought', indicators: ['No security process', 'Reactive only', 'No training'] },
      { level: 2, description: 'Basic security', indicators: ['Some security reviews', 'Basic scanning', 'Awareness training'] },
      { level: 3, description: 'Proactive security', indicators: ['Security in SDLC', 'Automated scanning', 'Threat modeling'] },
      { level: 4, description: 'Security embedded', indicators: ['DevSecOps', 'Security champions', 'Bug bounty program'] },
      { level: 5, description: 'Security excellence', indicators: ['Zero trust architecture', 'Red team exercises', 'Security culture'] },
    ],
  },
  {
    id: 'data',
    name: 'Data Engineering Function',
    icon: '📊',
    weight: 0.10,
    criteria: [
      { level: 1, description: 'No data infrastructure', indicators: ['Siloed data', 'No pipelines', 'Manual exports'] },
      { level: 2, description: 'Basic data systems', indicators: ['Some ETL', 'Basic warehouse', 'Ad-hoc analytics'] },
      { level: 3, description: 'Data platform', indicators: ['Automated pipelines', 'Data catalog', 'Self-service BI'] },
      { level: 4, description: 'Advanced data', indicators: ['Real-time streaming', 'ML pipelines', 'Data governance'] },
      { level: 5, description: 'Data excellence', indicators: ['Data mesh', 'MLOps', 'Data products'] },
    ],
  },
];

export default function CTORatingFrameworkPage() {
  // Assessment mode: 'both', 'cto', 'team'
  const [mode, setMode] = useState<'both' | 'cto' | 'team'>('both');
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'cto-rating-framework',
    toolCategory: 'maturity',
  });

  // CTO scores
  const [ctoLevels, setCtoLevels] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    CTO_CATEGORIES.forEach(cat => {
      initial[cat.id] = 3;
    });
    return initial;
  });

  // Team scores
  const [teamLevels, setTeamLevels] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    TEAM_CATEGORIES.forEach(cat => {
      initial[cat.id] = 3;
    });
    return initial;
  });

  // Calculate weighted CTO score
  const ctoMaturity = useMemo(() => {
    const weightedSum = CTO_CATEGORIES.reduce((sum, cat) => {
      return sum + (ctoLevels[cat.id] * cat.weight);
    }, 0);
    const level = Math.max(1, Math.min(5, Math.round(weightedSum)));
    const maturity = MATURITY_LEVELS[level - 1];

    return { ...maturity, avgScore: weightedSum, level };
  }, [ctoLevels]);

  // Calculate weighted Team score
  const teamMaturity = useMemo(() => {
    const weightedSum = TEAM_CATEGORIES.reduce((sum, cat) => {
      return sum + (teamLevels[cat.id] * cat.weight);
    }, 0);
    const level = Math.max(1, Math.min(5, Math.round(weightedSum)));
    const maturity = MATURITY_LEVELS[level - 1];

    return { ...maturity, avgScore: weightedSum, level };
  }, [teamLevels]);

  // Calculate overall combined score (CTO 50%, Team 50%)
  const overallMaturity = useMemo(() => {
    const combined = (ctoMaturity.avgScore * 0.5) + (teamMaturity.avgScore * 0.5);
    const level = Math.max(1, Math.min(5, Math.round(combined)));
    const maturity = MATURITY_LEVELS[level - 1];

    return { ...maturity, avgScore: combined, level };
  }, [ctoMaturity, teamMaturity]);

  // Recommendations
  const recommendations = useMemo(() => {
    const recs: Array<{ text: string; impact: number }> = [];

    // CTO recommendations
    CTO_CATEGORIES.forEach(cat => {
      if (ctoLevels[cat.id] < 3) {
        const gap = 3 - ctoLevels[cat.id];
        recs.push({
          text: `CTO: Improve ${cat.name} (currently Level ${ctoLevels[cat.id]})`,
          impact: gap * cat.weight
        });
      }
    });

    // Team recommendations
    TEAM_CATEGORIES.forEach(cat => {
      if (teamLevels[cat.id] < 3) {
        const gap = 3 - teamLevels[cat.id];
        recs.push({
          text: `Team: Improve ${cat.name} (currently Level ${teamLevels[cat.id]})`,
          impact: gap * cat.weight
        });
      }
    });

    return recs
      .sort((a, b) => b.impact - a.impact)
      .slice(0, 6)
      .map(r => r.text);
  }, [ctoLevels, teamLevels]);

  // AI Roadmap placeholder
  const aiRoadmapReady = useMemo(() => {
    if (mode === 'both') return overallMaturity.level <= 3;
    if (mode === 'cto') return ctoMaturity.level <= 3;
    if (mode === 'team') return teamMaturity.level <= 3;
    return false;
  }, [mode, overallMaturity, ctoMaturity, teamMaturity]);

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const allGaps: Array<{ id: string; title: string; severity: 'critical' | 'high' | 'medium' | 'low'; description: string }> = [];
    const allRecs: Array<{ id: string; title: string; priority: 'high' | 'medium' | 'low'; description: string }> = [];
    const scores: Record<string, number> = {};

    // Process CTO categories
    if (mode === 'both' || mode === 'cto') {
      CTO_CATEGORIES.forEach(cat => {
        const level = ctoLevels[cat.id];
        scores[`cto-${cat.id}`] = (level / 5) * 100;
        if (level < 3) {
          allGaps.push({
            id: `cto-${cat.id}`,
            title: `CTO ${cat.name} at Level ${level}`,
            severity: level === 1 ? 'critical' : 'high',
            description: cat.criteria[level - 1].description,
          });
        }
        if (level < 5) {
          allRecs.push({
            id: `cto-${cat.id}`,
            title: `Improve CTO ${cat.name}`,
            priority: level < 2 ? 'high' : level < 3 ? 'high' : 'medium',
            description: `Advance from Level ${level} to Level ${level + 1}: ${cat.criteria[level].description}`,
          });
        }
      });
    }

    // Process Team categories
    if (mode === 'both' || mode === 'team') {
      TEAM_CATEGORIES.forEach(cat => {
        const level = teamLevels[cat.id];
        scores[`team-${cat.id}`] = (level / 5) * 100;
        if (level < 3) {
          allGaps.push({
            id: `team-${cat.id}`,
            title: `Team ${cat.name} at Level ${level}`,
            severity: level === 1 ? 'critical' : 'high',
            description: cat.criteria[level - 1].description,
          });
        }
        if (level < 5) {
          allRecs.push({
            id: `team-${cat.id}`,
            title: `Improve Team ${cat.name}`,
            priority: level < 2 ? 'high' : level < 3 ? 'high' : 'medium',
            description: `Advance from Level ${level} to Level ${level + 1}: ${cat.criteria[level].description}`,
          });
        }
      });
    }

    // Sort and limit recommendations
    const sortedRecs = allRecs
      .sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      })
      .slice(0, 8);

    const overallScore = mode === 'both' ? (overallMaturity.avgScore / 5) * 100 :
                         mode === 'cto' ? (ctoMaturity.avgScore / 5) * 100 :
                         (teamMaturity.avgScore / 5) * 100;

    const modeName = mode === 'both' ? 'CTO + Team' : mode === 'cto' ? 'CTO Personal' : 'Team Functions';

    const assessment = await saveResult({
      name: `CTO Rating (${modeName}) - ${new Date().toLocaleDateString()}`,
      score_overall: overallScore,
      scores,
      answers: { mode, ctoLevels, teamLevels },
      gaps: allGaps,
      recommendations: sortedRecs,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-4xl">🎯</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">
              CTO Rating Framework
            </h1>
          </div>
          <p className="text-xl text-foreground-secondary">
            Comprehensive assessment of your effectiveness as a CTO and your team&apos;s capabilities
          </p>
        </div>

        <nav className="mb-8 text-sm text-foreground-tertiary">
          <Link href="/" className="hover:text-accent transition-colors">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">CTO Rating Framework</span>
        </nav>

        {/* Instructions */}
        <div className="bg-accent-muted border border-border rounded-lg p-6 mb-8">
          <h3 className="font-bold text-accent mb-2">How to use this framework</h3>
          <ol className="text-foreground-secondary text-sm space-y-2 list-decimal list-inside">
            <li>Select assessment mode: Both, CTO only, or Team only</li>
            <li>For each category, select the maturity level that best describes current state</li>
            <li>Review overall maturity scores with weighted calculations</li>
            <li>Use recommendations to create improvement roadmap</li>
            <li><span className="text-accent font-semibold">Future:</span> Logged-in users will get AI-driven personalized roadmaps</li>
          </ol>
        </div>

        {/* Mode Selector */}
        <div className="flex gap-4 mb-8" role="group" aria-label="Assessment mode selector">
          <button
            onClick={() => setMode('both')}
            aria-pressed={mode === 'both'}
            className={`flex-1 px-6 py-4 rounded-lg font-semibold transition-all ${
              mode === 'both'
                ? 'bg-accent text-accent-foreground'
                : 'bg-surface border border-border text-foreground hover:border-border-muted0'
            }`}
          >
            <div className="text-2xl mb-1">👤 + 👥</div>
            <div>Both: CTO + Team</div>
          </button>
          <button
            onClick={() => setMode('cto')}
            aria-pressed={mode === 'cto'}
            className={`flex-1 px-6 py-4 rounded-lg font-semibold transition-all ${
              mode === 'cto'
                ? 'bg-accent text-accent-foreground'
                : 'bg-surface border border-border text-foreground hover:border-border-muted0'
            }`}
          >
            <div className="text-2xl mb-1">👤</div>
            <div>CTO Only</div>
          </button>
          <button
            onClick={() => setMode('team')}
            aria-pressed={mode === 'team'}
            className={`flex-1 px-6 py-4 rounded-lg font-semibold transition-all ${
              mode === 'team'
                ? 'bg-accent text-accent-foreground'
                : 'bg-surface border border-border text-foreground hover:border-border-muted0'
            }`}
          >
            <div className="text-2xl mb-1">👥</div>
            <div>Team Only</div>
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column: Assessment Forms */}
          <div className="lg:col-span-2 space-y-8">
            {/* CTO Assessment */}
            {(mode === 'both' || mode === 'cto') && (
              <div>
                <div className="mb-6 pb-4 border-b border-border">
                  <h2 className="text-3xl font-bold text-foreground flex items-center gap-3">
                    <span>👤</span>
                    <span>CTO Personal Assessment</span>
                  </h2>
                  <p className="text-foreground-tertiary mt-2">Evaluate your personal effectiveness as a CTO</p>
                </div>

                <div className="space-y-6">
                  {CTO_CATEGORIES.map(category => (
                    <CategoryAssessment
                      key={category.id}
                      category={category}
                      currentLevel={ctoLevels[category.id]}
                      onChange={(level) => setCtoLevels(prev => ({ ...prev, [category.id]: level }))}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Team Assessment */}
            {(mode === 'both' || mode === 'team') && (
              <div className={mode === 'both' ? 'mt-12' : ''}>
                <div className="mb-6 pb-4 border-b border-border">
                  <h2 className="text-3xl font-bold text-foreground flex items-center gap-3">
                    <span>👥</span>
                    <span>CTO Team Functions</span>
                  </h2>
                  <p className="text-foreground-tertiary mt-2">Evaluate your team&apos;s functional capabilities (not individuals)</p>
                </div>

                <div className="space-y-6">
                  {TEAM_CATEGORIES.map(category => (
                    <CategoryAssessment
                      key={category.id}
                      category={category}
                      currentLevel={teamLevels[category.id]}
                      onChange={(level) => setTeamLevels(prev => ({ ...prev, [category.id]: level }))}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Results Dashboard */}
          <div className="space-y-6">
            <div className="bg-surface rounded-lg border border-border p-6 sticky top-4">
              {/* Overall Score (if both) */}
              {mode === 'both' && (
                <>
                  <h3 className="text-xl font-bold text-foreground mb-4">Overall Maturity</h3>
                  <div className="text-center py-6 mb-4 bg-background rounded-lg border border-border">
                    <div className="text-6xl font-bold text-accent mb-2">
                      {overallMaturity.level}
                    </div>
                    <div className={`text-2xl font-bold ${overallMaturity.color} mb-2`}>
                      {overallMaturity.name}
                    </div>
                    <div className="text-sm text-foreground-secondary">{overallMaturity.description}</div>
                    <div className="mt-4 text-sm text-foreground-tertiary">
                      Combined Score: {overallMaturity.avgScore.toFixed(2)}
                    </div>
                  </div>
                  <div className="border-t border-border-muted pt-4 mb-4"></div>
                </>
              )}

              {/* CTO Score */}
              {(mode === 'both' || mode === 'cto') && (
                <div className="mb-6">
                  <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                    <span>👤</span>
                    <span>CTO Maturity</span>
                  </h4>
                  <div className={`text-center py-4 rounded-lg border ${ctoMaturity.borderColor} ${ctoMaturity.bgColor}`}>
                    <div className="text-4xl font-bold text-accent mb-1">
                      Level {ctoMaturity.level}
                    </div>
                    <div className={`text-lg font-bold ${ctoMaturity.color}`}>
                      {ctoMaturity.name}
                    </div>
                    <div className="text-xs text-foreground-tertiary mt-2">
                      Weighted: {ctoMaturity.avgScore.toFixed(2)}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {CTO_CATEGORIES.map(cat => (
                      <div key={cat.id} className="flex justify-between items-center text-sm">
                        <span className="text-foreground-secondary flex items-center gap-2">
                          <span>{cat.icon}</span>
                          <span className="truncate">{cat.name}</span>
                        </span>
                        <span className="text-accent font-bold whitespace-nowrap ml-2">
                          L{ctoLevels[cat.id]} <span className="text-foreground-muted">({(cat.weight * 100).toFixed(0)}%)</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Team Score */}
              {(mode === 'both' || mode === 'team') && (
                <div className={mode === 'both' ? 'border-t border-border-muted pt-4' : ''}>
                  <h4 className="font-bold text-foreground mb-3 flex items-center gap-2">
                    <span>👥</span>
                    <span>Team Maturity</span>
                  </h4>
                  <div className={`text-center py-4 rounded-lg border ${teamMaturity.borderColor} ${teamMaturity.bgColor}`}>
                    <div className="text-4xl font-bold text-accent mb-1">
                      Level {teamMaturity.level}
                    </div>
                    <div className={`text-lg font-bold ${teamMaturity.color}`}>
                      {teamMaturity.name}
                    </div>
                    <div className="text-xs text-foreground-tertiary mt-2">
                      Weighted: {teamMaturity.avgScore.toFixed(2)}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {TEAM_CATEGORIES.map(cat => (
                      <div key={cat.id} className="flex justify-between items-center text-sm">
                        <span className="text-foreground-secondary flex items-center gap-2">
                          <span>{cat.icon}</span>
                          <span className="truncate">{cat.name}</span>
                        </span>
                        <span className="text-accent font-bold whitespace-nowrap ml-2">
                          L{teamLevels[cat.id]} <span className="text-foreground-muted">({(cat.weight * 100).toFixed(0)}%)</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {recommendations.length > 0 && (
                <div className="border-t border-border-muted pt-4 mt-4">
                  <h4 className="font-semibold text-accent mb-3">Priority Improvements:</h4>
                  <ul className="space-y-2">
                    {recommendations.map((rec, i) => (
                      <li key={i} className="text-xs text-foreground-secondary">• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* AI Roadmap CTA */}
              {aiRoadmapReady && (
                <div className="mt-6 p-4 bg-accent-muted rounded-lg border border-border">
                  <h4 className="font-semibold text-accent mb-2 flex items-center gap-2">
                    <span>🤖</span>
                    <span>AI-Driven Roadmap</span>
                  </h4>
                  <p className="text-xs text-foreground-secondary mb-3">
                    Get personalized improvement plans, resource recommendations, and milestone tracking.
                  </p>
                  <button
                    disabled
                    className="w-full px-4 py-2 bg-cream/10 text-foreground-muted rounded font-semibold cursor-not-allowed"
                  >
                    Coming Soon (Login Required)
                  </button>
                </div>
              )}

              {/* Save to Command Center */}
              {commandCenterEnabled && !saved && (
                <div className="border-t border-border-muted pt-4 mt-4">
                  <button
                    onClick={handleSaveToCommandCenter}
                    disabled={saving}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save to Command Center'}
                  </button>
                </div>
              )}
              {saved && (
                <div className="border-t border-border-muted pt-4 mt-4">
                  <div className="w-full px-4 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved to Command Center
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Maturity Level Reference */}
        <div className="mt-12 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Maturity Level Reference</h3>
          <div className="grid md:grid-cols-5 gap-4">
            {MATURITY_LEVELS.map(level => (
              <div key={level.level} className={`p-4 rounded border ${level.borderColor} ${level.bgColor}`}>
                <div className="text-2xl font-bold text-accent mb-2">Level {level.level}</div>
                <div className={`font-semibold ${level.color} mb-1 text-sm`}>{level.name}</div>
                <div className="text-xs text-foreground-secondary">{level.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Weighting Explanation */}
        <div className="mt-8 bg-surface rounded-lg border border-border p-6">
          <h3 className="text-lg font-bold text-foreground mb-4">Understanding Weighted Scores</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-accent mb-3">CTO Categories (Personal)</h4>
              <ul className="space-y-2 text-sm text-foreground-secondary">
                {CTO_CATEGORIES.map(cat => (
                  <li key={cat.id} className="flex justify-between">
                    <span>{cat.icon} {cat.name}</span>
                    <span className="text-accent font-bold">{(cat.weight * 100).toFixed(0)}%</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-accent mb-3">Team Functions</h4>
              <ul className="space-y-2 text-sm text-foreground-secondary">
                {TEAM_CATEGORIES.map(cat => (
                  <li key={cat.id} className="flex justify-between">
                    <span>{cat.icon} {cat.name}</span>
                    <span className="text-accent font-bold">{(cat.weight * 100).toFixed(0)}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-xs text-foreground-tertiary mt-4">
            Overall score combines CTO (50%) and Team (50%) weighted scores. Categories are weighted based on relative importance to CTO effectiveness.
          </p>
        </div>
      </div>
    </div>
  );
}

// Category Assessment Component
function CategoryAssessment({
  category,
  currentLevel,
  onChange,
}: {
  category: AssessmentCategory;
  currentLevel: number;
  onChange: (level: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 flex-1">
          <span className="text-3xl">{category.icon}</span>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-foreground">{category.name}</h3>
            <div className="text-sm text-foreground-tertiary">
              Weight: {(category.weight * 100).toFixed(0)}%
            </div>
          </div>
        </div>
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-accent hover:text-foreground transition-colors"
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
          aria-expanded={expanded}
        >
          {expanded ? '▼' : '▶'}
        </button>
      </div>

      {/* Level Selector */}
      <div className="flex gap-2 mb-4">
        {[1, 2, 3, 4, 5].map(level => {
          const maturity = MATURITY_LEVELS[level - 1];
          return (
            <button
              key={level}
              onClick={() => onChange(level)}
              className={`flex-1 px-3 py-2 rounded font-semibold transition-all ${
                currentLevel === level
                  ? `${maturity.bgColor} border ${maturity.borderColor} ${maturity.color}`
                  : 'bg-background border border-border-muted text-foreground-tertiary hover:border-border'
              }`}
            >
              {level}
            </button>
          );
        })}
      </div>

      {/* Current Level Info */}
      <div className="p-3 bg-background rounded border border-border-muted">
        <div className="font-semibold text-foreground mb-1">
          Level {currentLevel}: {category.criteria[currentLevel - 1].description}
        </div>
        <ul className="text-sm text-foreground-secondary space-y-1">
          {category.criteria[currentLevel - 1].indicators.map((indicator, i) => (
            <li key={i}>• {indicator}</li>
          ))}
        </ul>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 space-y-3">
          <h4 className="font-semibold text-accent text-sm">All Maturity Levels:</h4>
          {category.criteria.map((criterion, i) => (
            <div
              key={i}
              className={`p-3 rounded border ${
                currentLevel === criterion.level
                  ? 'border-accent bg-accent-muted'
                  : 'border-border-muted bg-background/50'
              }`}
            >
              <div className="font-semibold text-foreground text-sm mb-1">
                Level {criterion.level}: {criterion.description}
              </div>
              <ul className="text-xs text-foreground-tertiary space-y-1">
                {criterion.indicators.map((indicator, j) => (
                  <li key={j}>• {indicator}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
